import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Flex } from "rebass";
import { navigate } from "gatsby";
import { bottom, display, height, minWidth, width } from "styled-system";
import { keys } from "ramda";
import { trackCustomEvent } from "gatsby-plugin-google-analytics";

import { BlogH3, BlogP, H1, H5, Span } from "../Typography";
import Frame from "../Frame/BlogFrame";
import HeaderFrame from "../Frame/Frame";
import Input from "../Input";
import SocialSidebar from "../SocialSidebar";
import Image from "../Image/Image";
import Video from "../Video";
import Navigation from "../Navigation";
import SocialIcons from "../SocialIcons";

import colors from "../../utils/theme/colors";
import slugify from "../../utils/slugify";
import { groupByCategory } from "../../selectors/selectors";
import Showreel from "../../assets/video/showreel.mp4";
import ScrollDownIcon from "../../assets/icons/scrollDown.svg";
import Link from "../Link";

const BlogLayout = ({
  children,
  allBlogs,
  slug,
  socialTitle,
  socialLinks,
  bgMobileFluid,
  longTitle
}) => {
  const handleSearch = e => {
    if (e.key === "Enter" && e.currentTarget.value) {
      navigate(`/blog-search/?search=${e.currentTarget.value}`);
    }
  };
  const blogs = groupByCategory(allBlogs);
  const categoryName = keys(blogs).sort();
  return (
    <>
      <HeaderFrame
        pt={[3, 5, null, null]}
        pb={[2, 6, 8, 11]}
        color="white"
        height="100vh"
        css={`
          width: 100%;
          overflow: hidden;
          position: relative;
          @media (max-width: 828px) and (orientation: landscape) {
            min-height: 820px;
            overflow: hidden;
          }
          @media (max-width: 767px) and (orientation: landscape) {
            min-height: 670px;
            overflow: hidden;
          }
        `}
      >
        <Image
          fluid={bgMobileFluid}
          css={`
            width: 100%;
            z-index: -1;
            left: 0px;
            top: 0px;
            right: 0px;
            bottom: 0px;
            margin: auto;
            position: absolute !important;
            display: none !important;
            @media (max-width: 767px) {
              display: block !important;
            }
          `}
        />
        <Video
          src={Showreel}
          autoPlay
          muted
          loop
          display={["none", "block", "block", null]}
          css={`
            width: 100%;
            z-index: -2;
            left: 0px;
            top: 0px;
            right: 0;
            bottom: 0px;
            margin: auto;
            position: absolute;
            ${display}
            @media (max-width: 1920px) {
              min-width: 125% !important;
              left: -250px;
              top: -50px;
            }
            @media (max-width: 1366px) {
              min-width: 135% !important;
              left: -450px;
              top: -50px;
            }
            @media (max-width: 1280px) {
              min-width: 155% !important;
              left: -400px;
              top: -50px;
            }
            @media (max-width: 1024px) {
              min-width: 240% !important;
              left: -1100px;
              top: -50px;
            }
            @media (max-width: 768px) {
              min-width: 240% !important;
              left: -780px;
              top: -50px;
            }
          `}
        />
        <Navigation />
        <Flex
          justifyContent="space-between"
          height="calc(100% - 30px)"
          flexDirection={["column", "row", null, null]}
        >
          <Flex
            flexDirection="column"
            justifyContent="center"
            pt={[7, 0, null]}
          >
            <H1 mb={2}>
              Video creatives
              <br />
              for mobile user
              <br />
              acquisition.
            </H1>
            <H5
              width={[1, 0.7, 1, null]}
              mb={40}
              css={{
                // opacity: isStartAnimation ? 1 : 0,
                // transform: isStartAnimation
                //   ? "translateY(0px)"
                //   : "translateY(100px)",
                transition: "all 0.4s ease-out",
                transitionDelay: "0.2s"
              }}
            >
              {longTitle}
            </H5>
            <Link
              to="#contactUs"
              width="max-content"
              onClick={() => {
                trackCustomEvent({
                  category: "Click",
                  action: "HitUsUp",
                  label: "MainPage"
                });
              }}
              css={{
                // opacity: isStartAnimation ? 1 : 0,
                // transform: isStartAnimation
                //   ? "translateY(0px)"
                //   : "translateY(100px)",
                transition: "all 0.4s ease-out",
                transitionDelay: "0.3s"
              }}
            >
              <Button
                variant="headerButton"
                css={`
                  @media (min-width: 1280px) {
                    :hover {
                      background-color: transparent;
                      color: white;
                    }
                  }
                `}
              >
                hit us up
              </Button>
            </Link>
          </Flex>
          <Flex
            flexDirection="column"
            justifyContent="flex-end"
            alignItems="flex-end"
            css={`
              min-width: auto !important;
            `}
          >
            <SocialIcons
              css={`
                display: flex !important;
                @media (max-width: 767px) {
                  display: none !important;
                }
              `}
              iconProps={{
                mb: [0, "25px", null, null],
                css: {
                  ":last-child": {
                    marginBottom: "0px"
                  }
                }
              }}
            />
          </Flex>
        </Flex>
        <Box
          bottom={["20px", "60px", "80px", null]}
          width={["16px", "20px", null, null]}
          height={["55.5px", "70px", null, null]}
          css={`
            position: absolute;
            ${bottom}
            ${width}
            ${height}
          `}
        >
          <ScrollDownIcon height="100%" width="100%" />
        </Box>
      </HeaderFrame>
      <Frame
        pt={[9, 12, 15, 18]}
        pb={[8, 15, 20, 25]}
        as="section"
        id="main"
        color={colors.secondary2}
      >
        <Flex flexDirection={["column", "row", "row", "row"]}>
          <Box
            mb="504px"
            display={["none", "none", "block", "block"]}
            css={`
              ${display}
              max-width: 100px;
              transform: translateX(0);
              @media (min-width: 1500px) {
                transform: translateX(-50px);
                margin-left: -50px;
                margin-right: 50px;
              }
            `}
          >
            <SocialSidebar
              socialLinks={socialLinks}
              url={slug}
              title={socialTitle}
            />
          </Box>
          {children}
          <Box mt={[5, 0, 0, 0]} mb={[2, "650px", "650px", "650px"]}>
            <Box
              css={`
                ${minWidth};
                position: -webkit-sticky;
                position: sticky;
                top: 40px;
                @media (max-width: 768px) {
                  transform: translateX(0px);
                }
              `}
            >
              <Input
                mt={0}
                mb={3}
                width={["210px", "200px", "210px", "210px"]}
                type="text"
                placeholder="Search"
                color={colors.secondary2}
                focusColor={colors.secondary2}
                onKeyPress={handleSearch}
              />
              <Box mb={2}>
                <BlogH3>categories</BlogH3>
              </Box>
              {categoryName.map(category => (
                <Link
                  key={category}
                  style={{ textDecoration: "none", color: colors.secondary2 }}
                  to={`/blog/${slugify(category)}`}
                  css="width: 100%; "
                >
                  <BlogP
                    px={[0, 2]}
                    py="10px"
                    css={{
                      position: "relative",
                      "&:hover": {
                        cursor: "pointer",
                        backgroundColor: "rgb(215, 221, 222)",
                        "#categoryCount": {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    {category}
                    <Span
                      id="categoryCount"
                      css={{ position: "absolute", right: "50px", opacity: 0 }}
                    >
                      {`(${blogs[category].length})`}
                    </Span>
                  </BlogP>
                </Link>
              ))}
            </Box>
          </Box>
        </Flex>
      </Frame>
    </>
  );
};

BlogLayout.propTypes = {
  children: PropTypes.node.isRequired,
  allBlogs: PropTypes.arrayOf(PropTypes.object).isRequired,
  socialLinks: PropTypes.arrayOf(PropTypes.object).isRequired,
  socialTitle: PropTypes.string.isRequired,
  longTitle: PropTypes.string,
  slug: PropTypes.string.isRequired,
  bgMobileFluid: PropTypes.shape().isRequired
};

BlogLayout.defaultProps = {
  longTitle: null
};

export default BlogLayout;
